import { useEffect, useState } from 'react';
import type { FormPermissionsResults } from '../@types/form-permissions';
import { useLazyFormPermissionsQuery } from '../redux/services/api';
import { batchPromises, makeCancelable } from '../utils/promise-util';

interface FetchAllFormsPermissionsState {
  isLoading: boolean;
  canExportRecord: boolean;
}

/**
 * A hook that fetches All Forms permissions by fetching all the forms permissions for that module
 *
 * @param {Array<number>} allFormIdsPerModule - a list of form ids to fetch permissions for.
 *
 * @returns {{isLoading: boolean, canExportRecord: boolean}}
 */
const useFetchAllFormsPermissions = (allFormIdsPerModule: Array<number>, isAllForms = false) => {
  const [state, setState] = useState<FetchAllFormsPermissionsState>({
    isLoading: false,
    canExportRecord: false,
  });
  const [listOfFormIdsWithCreatePermission, setListOfFormIdsWithCreatePermission] = useState<number[]>([]);

  const [getFormPermissions] = useLazyFormPermissionsQuery();

  const fetchFormPermissions = async (allFormIdsPerModule: Array<number>) => {
    const formPermissionsRequests: Array<() => Promise<FormPermissionsResults>> = []; // holds the list of form permissions requests to be made

    allFormIdsPerModule.forEach((id) =>
      formPermissionsRequests.push(() => {
        return getFormPermissions(id, true).unwrap();
      })
    );

    // fetch form permissionns in batches
    const promises = await batchPromises(formPermissionsRequests, formPermissionsRequests.length);

    // extract the fulfilled promises
    const fulfilledResponses: Array<FormPermissionsResults> = (
      promises.filter((promise) => promise.status === 'fulfilled') as PromiseFulfilledResult<FormPermissionsResults>[]
    ).map((promise) => promise.value);
    const formPermissions: Array<FormPermissionsResults> = [];
    fulfilledResponses.forEach((fulfilledResponse) => fulfilledResponse && formPermissions.push(fulfilledResponse));
    return formPermissions;
  };

  useEffect(() => {
    if (isAllForms) {
      const fetchFormsPermissionsPromise = makeCancelable(fetchFormPermissions(allFormIdsPerModule));

      setState({
        ...state,
        isLoading: true,
      });

      fetchFormsPermissionsPromise.promise
        .then((permissions) => {
          const canExportRecord = (permissions as Array<FormPermissionsResults>).some(
            (permission) => permission.canExportRecord === true
          );
          const listOfFormidsWithRecordCreatePermissions = (permissions as Array<FormPermissionsResults>)
            .filter((item) => item.canCreateRecord)
            .map((item) => item.formId!);
          setListOfFormIdsWithCreatePermission(listOfFormidsWithRecordCreatePermissions);
          setState({
            isLoading: false,
            canExportRecord,
          });
        })
        .catch(({ isCanceled }) => {
          if (isCanceled !== true) {
            // only update the state if the promise was not cancelled
            setState({
              ...state,
              isLoading: false,
            });
          }
        });
      return () => {
        fetchFormsPermissionsPromise.cancel();
      };
    }
  }, [allFormIdsPerModule, isAllForms]);

  return {
    ...state,
    listOfFormIdsWithCreatePermission,
  };
};

export default useFetchAllFormsPermissions;
